
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EnrichedVisitor } from "@/types/database";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface BlockCompanyDialogProps {
  visitor: EnrichedVisitor | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { companyName: string; domain: string; notes?: string; organizationId?: string }) => void;
  organizationId?: string;
}

export function BlockCompanyDialog({ 
  visitor, 
  isOpen, 
  onOpenChange, 
  onConfirm,
  organizationId 
}: BlockCompanyDialogProps) {
  const [notes, setNotes] = useState("");
  const [isBlocking, setIsBlocking] = useState(false);

  if (!visitor) return null;

  const handleConfirm = async () => {
    setIsBlocking(true);
    try {
      await onConfirm({
        companyName: visitor.company_name || "",
        domain: visitor.company_website || "",
        notes: notes,
        organizationId
      });
    } finally {
      setIsBlocking(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Block Company</DialogTitle>
          <DialogDescription>
            This company will no longer appear in your visitors list. You can unblock it later from settings.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <Label>Company</Label>
            <Input 
              value={visitor.company_name || ""} 
              disabled 
            />
          </div>
          <div>
            <Label>Domain</Label>
            <Input 
              value={visitor.company_website || ""} 
              disabled 
            />
          </div>
          <div className="space-y-2">
            <Label className="mb-2">Notes (Optional)</Label>
            <Textarea
              placeholder="Add notes about why this company is being blocked..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={handleConfirm}
            disabled={isBlocking}
          >
            {isBlocking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Block Company
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
